// Helper to get local YYYY-MM-DD string without UTC timezone drift
function getLocalDateString(d = new Date()) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Function to update the storefront estimate
async function updateCartDeliveryEstimate(forceFetch = false) {
  try {
    const todayStr = getLocalDateString();

    // 1. Check B2B approved status
    const b2bState = window.WhizShop?.b2bState || 'guest';
    console.log('[Delify] Current B2B State:', b2bState);
    if (b2bState !== 'approved') {
      console.log('[Delify] Customer is not in B2B approved state. Hiding estimate UI.');
      toggleEstimateUI(null);
      updateCartB2BPricing([], null, 'SEK', null);
      return;
    }

    if (forceFetch) {
      hideCartValuesDuringUpdate();
    }

    // 2. Get current cart items using localized routes if available
    const cartUrl = (window.Theme?.routes?.cart_url || '/cart') + '.js';
    console.log('[Delify] Fetching cart data from:', cartUrl);
    const cartResponse = await fetch(cartUrl);
    const cart = await cartResponse.json();
    console.log('[Delify] Fetched cart data:', cart);
    
    const updateUrl = window.Theme?.routes?.cart_update_url || '/cart/update';
    if (!cart.items || cart.items.length === 0) {
      console.log('[Delify] Cart is empty.');
      toggleEstimateUI(null);
      updateCartB2BPricing([], null, 'SEK', cart);
      sessionStorage.removeItem('delify_cart_state');
      // Clear cart attributes if empty
      if (cart.attributes && (cart.attributes.expected_delivery_date || cart.attributes.expected_delivery_calculated_date)) {
        console.log('[Delify] Clearing expected_delivery_date attributes for empty cart at URL:', updateUrl);
        const clearResponse = await fetch(updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ 
            attributes: { 
              'expected_delivery_date': '',
              'expected_delivery_calculated_date': ''
            } 
          })
        });
        const clearData = await clearResponse.json();
        console.log('[Delify] Clear cart attribute response:', clearData);
      }
      return;
    }

    // Validate existing cart attribute date
    const existingDate = cart.attributes?.expected_delivery_date;
    const calcDate = cart.attributes?.expected_delivery_calculated_date;
    const isExistingDateValid = Boolean(
      existingDate &&
      existingDate >= todayStr &&
      calcDate === todayStr
    );

    // If an existing cart attribute date is present but invalid (from a prior day or in the past), reset UI immediately
    if (existingDate && !isExistingDateValid) {
      console.log(`[Delify] Existing cart delivery date is invalid/expired (date: ${existingDate}, calculated: ${calcDate}, today: ${todayStr}). Resetting UI.`);
      toggleEstimateUI(null);
    }

    // Resolve current Shopify customer ID (if logged in) and format as Shopify GID
    let customerId = window.ShopifyCustomerId ? String(window.ShopifyCustomerId) : '';
    if (customerId && !customerId.startsWith('gid://')) {
      customerId = `gid://shopify/Customer/${customerId}`;
    }

    // Validate sessionStorage cache
    let cachedState = null;
    if (!forceFetch) {
      const cachedStateStr = sessionStorage.getItem('delify_cart_state');
      if (cachedStateStr) {
        try {
          const parsed = JSON.parse(cachedStateStr);
          // Invalidate cache if customer or currency changed
          if (parsed.customerId && customerId && parsed.customerId !== customerId) {
            console.log('[Delify] Cache belonged to different customer. Invalidating cache.');
            sessionStorage.removeItem('delify_cart_state');
          } else if (parsed.currency && (cart.currency || 'SEK') !== parsed.currency) {
            console.log('[Delify] Cache currency mismatch. Invalidating cache.');
            sessionStorage.removeItem('delify_cart_state');
          } else if (!parsed.cachedDate || parsed.cachedDate !== todayStr) {
            console.log(`[Delify] Cached estimate is from a previous day (${parsed.cachedDate} vs today ${todayStr}). Retaining prices but will refresh delivery date.`);
            parsed.estimate = null;
            cachedState = parsed;
          } else if (parsed.estimate && parsed.estimate < todayStr) {
            console.log(`[Delify] Cached estimate date is in the past (${parsed.estimate} < today ${todayStr}). Clearing expired date.`);
            parsed.estimate = null;
            cachedState = parsed;
          } else {
            cachedState = parsed;
          }
        } catch (e) {
          console.error('[Delify] Error parsing cached state:', e);
          sessionStorage.removeItem('delify_cart_state');
        }
      }
    }

    // Check if current cart items (SKUs & quantities) match cached pricing
    const isCartMatchingCache = (function() {
      if (!cachedState || !cachedState.prices || !Array.isArray(cachedState.prices.items)) return false;
      if (!cart || !cart.items || cart.items.length === 0) return false;

      const validCartItems = cart.items.filter(i => i.sku && typeof i.sku === 'string' && i.sku.trim() !== '');
      if (validCartItems.length === 0) return false;

      // Check against cached cartItems summary if available
      if (cachedState.cartItems && Array.isArray(cachedState.cartItems)) {
        if (cachedState.cartItems.length !== validCartItems.length) return false;
        for (const item of validCartItems) {
          const match = cachedState.cartItems.find(ci => ci.sku === item.sku && ci.quantity === item.quantity);
          if (!match) return false;
        }
        return true;
      }

      // Fallback check against prices.items mapping
      const cachedItems = cachedState.prices.items;
      if (validCartItems.length !== cachedItems.length) return false;

      for (const item of validCartItems) {
        const cachedItem = cachedItems.find(ci => ci.sku === item.sku);
        if (!cachedItem) return false;
        if (cachedItem.quantity != null && cachedItem.quantity !== item.quantity) return false;
      }

      return true;
    })();

    if (isCartMatchingCache && cachedState.prices) {
      // Sync cart token in cache if it changed due to B2B location switch
      if (cachedState.token !== cart.token) {
        cachedState.token = cart.token;
        sessionStorage.setItem('delify_cart_state', JSON.stringify(cachedState));
      }

      // If cached date is valid for today, apply estimate and prices immediately and skip backend round-trip
      if (cachedState.cachedDate === todayStr && (existingDate || cachedState.estimate)) {
        console.log('[Delify] Cart items and date match cached state. Using cached estimate & prices.');
        toggleEstimateUI(existingDate || cachedState.estimate);
        updateCartB2BPricing(
          cachedState.prices.items,
          cachedState.prices.subtotal,
          cachedState.prices.currency || 'SEK',
          cart,
          cachedState.prices.original_subtotal,
          cachedState.prices.discount_total
        );
        return;
      }

      // If prices match but date needs refresh, render prices immediately to prevent blank prices while fetching
      console.log('[Delify] Rendering cached prices while refreshing delivery estimate date in background.');
      updateCartB2BPricing(
        cachedState.prices.items,
        cachedState.prices.subtotal,
        cachedState.prices.currency || 'SEK',
        cart,
        cachedState.prices.original_subtotal,
        cachedState.prices.discount_total
      );
    }

    console.log('[Delify] Resolved Shopify Customer ID GID:', customerId);

    // 4. Prepare payload (SKU and quantity) - filtering out items with blank SKUs
    const validItems = cart.items
      .filter(item => item.sku && typeof item.sku === 'string' && item.sku.trim() !== '')
      .map(item => ({
        sku: item.sku,
        quantity: item.quantity
      }));

    console.log('[Delify] Valid items found for estimate:', JSON.stringify(validItems));

    if (validItems.length === 0) {
      console.log('[Delify] No items with valid SKUs in the cart.');
      toggleEstimateUI(null);
      updateCartB2BPricing([], null, 'SEK', cart);
      sessionStorage.removeItem('delify_cart_state');
      return;
    }

    const auth = window.WHIZSHOP_AUTH || {};
    const payload = {
      customerId: customerId,
      timestamp: auth.timestamp || null,
      signature: auth.signature || null,
      items: validItems
    };

    // 5. Post to backend
    console.log('[Delify] Fetching estimate from backend with payload:', JSON.stringify(payload));
    const estimateResponse = await fetch('https://whizshop-backend.fly.dev/api/checkout/estimate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!estimateResponse.ok) {
      throw new Error(`Estimate API responded with status: ${estimateResponse.status}`);
    }
    const data = await estimateResponse.json();
    console.log('[Delify] Estimate API response data:', data);
    
    // Guard against any date returned in the past
    let resolvedDateStr = data.expected_delivery_date || null;
    if (resolvedDateStr && resolvedDateStr < todayStr) {
      console.warn(`[Delify] Backend returned an expected delivery date in the past (${resolvedDateStr} < today ${todayStr}). Ignoring it.`);
      resolvedDateStr = null;
    }

    // 6. Update the UI
    toggleEstimateUI(resolvedDateStr);
    updateCartB2BPricing(
      data.items,
      data.cart_subtotal,
      data.currency || 'SEK',
      cart,
      data.cart_original_subtotal,
      data.cart_discount_total
    );

    // Save to sessionStorage cache with today's date
    sessionStorage.setItem('delify_cart_state', JSON.stringify({
      customerId: customerId,
      currency: data.currency || cart.currency || 'SEK',
      token: cart.token,
      itemCount: cart.item_count,
      totalPrice: cart.total_price,
      estimate: resolvedDateStr,
      cachedDate: todayStr,
      cartItems: validItems,
      prices: {
        items: data.items,
        subtotal: data.cart_subtotal,
        original_subtotal: data.cart_original_subtotal,
        discount_total: data.cart_discount_total,
        currency: data.currency || cart.currency || 'SEK'
      }
    }));

    // 7. Save the date to cart attributes so it propagates to checkouts and draft orders
    if (resolvedDateStr) {
      const needsUpdate = !cart.attributes ||
        cart.attributes.expected_delivery_date !== resolvedDateStr ||
        cart.attributes.expected_delivery_calculated_date !== todayStr;

      if (needsUpdate) {
        console.log('[Delify] Saving expected_delivery_date & expected_delivery_calculated_date attributes to cart URL:', updateUrl, 'value:', resolvedDateStr);
        const updateResponse = await fetch(updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            attributes: {
              'expected_delivery_date': resolvedDateStr,
              'expected_delivery_calculated_date': todayStr
            }
          })
        });
        if (updateResponse.ok) {
          const updateData = await updateResponse.json();
          console.log('[Delify] Successfully saved expected_delivery_date attributes to cart.', updateData);
        } else {
          console.error('[Delify] Failed to save expected_delivery_date attributes to cart. Status:', updateResponse.status);
        }
      }
    } else {
      if (cart.attributes && (cart.attributes.expected_delivery_date || cart.attributes.expected_delivery_calculated_date)) {
        console.log('[Delify] Clearing expected_delivery_date attributes from cart URL:', updateUrl);
        const updateResponse = await fetch(updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            attributes: {
              'expected_delivery_date': '',
              'expected_delivery_calculated_date': ''
            }
          })
        });
        if (updateResponse.ok) {
          const updateData = await updateResponse.json();
          console.log('[Delify] Successfully cleared expected_delivery_date attributes from cart.', updateData);
        } else {
          console.error('[Delify] Failed to clear expected_delivery_date attributes from cart. Status:', updateResponse.status);
        }
      }
    }
  } catch (error) {
    console.error('[Delify] Error calculating delivery estimate:', error);
    // Restore opacity on elements in case they were dimmed
    const totalValueEls = document.querySelectorAll('#whiz-b2b-cart-total-value, .whiz-b2b-cart-total-value');
    totalValueEls.forEach(el => { el.style.opacity = '1'; });
    const linePriceEls = document.querySelectorAll('.whiz-b2b-line-price, .whiz-b2b-unit-price');
    linePriceEls.forEach(el => { el.style.opacity = '1'; });
    const deliveryEls = document.querySelectorAll('#cart-drawer-delivery-estimate, #whiz-b2b-delivery-date-value');
    deliveryEls.forEach(el => { el.style.opacity = '1'; });
    const checkoutBtns = document.querySelectorAll('#checkout, button[name="checkout"], .b2b-btn-approved');
    checkoutBtns.forEach(el => {
      el.style.opacity = '1';
      el.style.pointerEvents = 'auto';
    });

    // ONLY retain cached pricing IF current cart items & quantities still strictly match the cache!
    // (e.g. changing delivery address or date check without cart item modifications).
    // If the customer modified quantities or added/removed products, the old cached prices are stale and invalid!
    if (isCartMatchingCache && cachedState && cachedState.prices) {
      console.log('[Delify] Cart items and quantities are unchanged. Safely retaining matching B2B pricing.');
      updateCartB2BPricing(
        cachedState.prices.items,
        cachedState.prices.subtotal,
        cachedState.prices.currency || cart.currency || 'SEK',
        cart,
        cachedState.prices.original_subtotal,
        cachedState.prices.discount_total
      );
    } else {
      // Cart was modified (quantities or items changed) and recalculation failed:
      // Stale cache must NOT be used because it would show wrong totals or outdated quantities.
      console.warn('[Delify] Cart was modified but pricing recalculation failed. Purging stale cache.');
      sessionStorage.removeItem('delify_cart_state');
      toggleEstimateUI(null);
      updateCartB2BPricing([], null, cart.currency || 'SEK', cart);
    }
  }
}

// UI Rendering Helper
function toggleEstimateUI(dateString) {
  const containers = document.querySelectorAll('#cart-drawer-delivery-estimate');
  console.log('[Delify] Toggling UI. Containers found:', containers.length, 'with dateString:', dateString);
  
  if (containers.length === 0) {
    // Retry once in the next animation frame in case morphing is not finished
    requestAnimationFrame(() => {
      const retryContainers = document.querySelectorAll('#cart-drawer-delivery-estimate');
      console.log('[Delify] Retry toggling UI. Containers found:', retryContainers.length);
      retryContainers.forEach(container => {
        applyEstimateToContainer(container, dateString);
      });
    });
    return;
  }

  containers.forEach(container => {
    applyEstimateToContainer(container, dateString);
  });
}

// Helper to dim prices, delivery estimate, and checkout button during update
function hideCartValuesDuringUpdate() {
  const totalValueEls = document.querySelectorAll('#whiz-b2b-cart-total-value, .whiz-b2b-cart-total-value');
  totalValueEls.forEach(el => {
    el.style.opacity = '0.5';
    el.style.transition = 'opacity 0.2s ease';
  });

  const linePriceEls = document.querySelectorAll('.whiz-b2b-line-price, .whiz-b2b-unit-price');
  linePriceEls.forEach((el) => {
    el.style.opacity = '0.5';
    el.style.transition = 'opacity 0.2s ease';
  });

  const deliveryEls = document.querySelectorAll('#cart-drawer-delivery-estimate, #whiz-b2b-delivery-date-value');
  deliveryEls.forEach((el) => {
    el.style.opacity = '0.5';
    el.style.transition = 'opacity 0.2s ease';
  });

  const checkoutBtns = document.querySelectorAll('#checkout, button[name="checkout"], .b2b-btn-approved');
  checkoutBtns.forEach((el) => {
    el.style.opacity = '0.5';
    el.style.transition = 'opacity 0.2s ease';
    el.style.pointerEvents = 'none';
  });
}

function applyEstimateToContainer(container, dateString) {
  const valueEl = container.querySelector('#whiz-b2b-delivery-date-value') || container;
  const isApproved = window.WhizShop?.b2bState === 'approved';

  if (!isApproved) {
    container.style.setProperty('display', 'none', 'important');
    if (valueEl !== container) {
      valueEl.innerHTML = '';
    } else {
      container.innerHTML = '';
    }
    return;
  }

  // Approved B2B customers: keep the component visible (display: flex) so layout does not jump!
  container.style.setProperty('display', 'flex', 'important');

  if (dateString) {
    const todayStr = getLocalDateString();
    if (dateString < todayStr) {
      console.warn(`[Delify] Refusing to display delivery date in the past: ${dateString} < today ${todayStr}`);
      dateString = null;
    }
  }

  if (dateString) {
    // Parse YYYY-MM-DD manually to avoid timezone shifting or browser parsing bugs
    const parts = dateString.split('-');
    let formattedDate = dateString;
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const dateObj = new Date(year, month, day);
      if (!isNaN(dateObj.getTime())) {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        formattedDate = dateObj.toLocaleDateString('en-US', options);
      }
    }
    
    if (valueEl !== container) {
      if (valueEl.innerHTML.trim() !== formattedDate) {
        valueEl.innerHTML = formattedDate;
      }
      valueEl.style.opacity = '1';
    } else {
      const targetHtml = `<strong>Expected Delivery:</strong> ${formattedDate}`;
      if (container.innerHTML.trim() !== targetHtml) {
        container.innerHTML = targetHtml;
      }
    }
    container.style.opacity = '1';
  } else {
    // Date is unknown or updating: keep component container visible, but blank out the value
    if (valueEl !== container) {
      valueEl.innerHTML = '';
    } else {
      container.innerHTML = '<strong>Expected Delivery:</strong> ';
    }
  }
}

// Immediately hide values whenever items or quantities are being modified, keeping component containers visible
document.addEventListener('quantity-selector:update', () => {
  console.log('[Delify] quantity-selector:update detected. Hiding values while recalculating.');
  hideCartValuesDuringUpdate();
});

document.addEventListener('input', (event) => {
  if (event.target.matches('.quantity__input, [name="updates[]"], input[name="quantity"]')) {
    console.log('[Delify] Quantity input detected. Hiding values while recalculating.');
    hideCartValuesDuringUpdate();
  }
});

document.addEventListener('change', (event) => {
  if (event.target.matches('.quantity__input, [name="updates[]"], input[name="quantity"]')) {
    console.log('[Delify] Quantity change detected. Hiding values while recalculating.');
    hideCartValuesDuringUpdate();
  }
});

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-cart-item-remove], .cart-items__remove, cart-remove-button, button[name="plus"], button[name="minus"]')) {
    console.log('[Delify] Cart item modification click detected. Hiding values while recalculating.');
    hideCartValuesDuringUpdate();
  }
});

// Listen to cart update events from theme components
document.addEventListener('cart:update', () => {
  console.log('[Delify] cart:update event detected. Hiding values and recalculating.');
  hideCartValuesDuringUpdate();
  setTimeout(() => updateCartDeliveryEstimate(true), 150);
});

// Listen to cart drawer dialog open event to re-apply pricing/estimates
document.addEventListener('dialog:open', (event) => {
  if (event.target.tagName?.toLowerCase() === 'cart-drawer-component') {
    console.log('[Delify] Cart drawer dialog:open event detected. Checking cache & refreshing B2B pricing/estimates.');
    const todayStr = getLocalDateString();
    const cachedStateStr = sessionStorage.getItem('delify_cart_state');
    if (cachedStateStr) {
      try {
        const cachedState = JSON.parse(cachedStateStr);
        if (cachedState.cachedDate !== todayStr || (cachedState.estimate && cachedState.estimate < todayStr)) {
          console.log('[Delify] Cached delivery date is from a previous day. Resetting date estimate while retaining cached pricing.');
          toggleEstimateUI(null);
          cachedState.estimate = null;
          sessionStorage.setItem('delify_cart_state', JSON.stringify(cachedState));
        }
      } catch (e) {}
    }
    updateCartDeliveryEstimate(false);
  }
}, { capture: true });

// Run once on load to initialize estimate, but do NOT fetch from Fly.dev backend if attribute is already present
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[Delify] DOMContentLoaded. Running estimate check.');
    updateCartDeliveryEstimate(false);
  });
} else {
  console.log('[Delify] readyState already complete. Running estimate check.');
  updateCartDeliveryEstimate(false);
}

// Helper to check if a discount is substantial (>= 2 in currency AND >= 1%)
function isSubstantialDiscount(discountAmount, discountPercent) {
  if (discountAmount == null || discountPercent == null) return false;
  return discountAmount >= 2 && discountPercent >= 1;
}

// Helper to format discount percentage nicely (e.g. 10 -> "10", 12.5 -> "12.5")
function formatDiscountPercent(val) {
  if (val == null) return '0';
  const rounded = Math.round(val * 10) / 10;
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
}

// B2B Pricing UI Update Helper
function updateCartB2BPricing(items, subtotal, currency, cart, originalSubtotal = null, discountTotal = null) {
  console.log('[Delify] updateCartB2BPricing called. Items in backend response:', items?.length, 'Subtotal:', subtotal, 'Currency:', currency, 'OriginalSubtotal:', originalSubtotal, 'DiscountTotal:', discountTotal);
  
  const isApproved = window.WhizShop?.b2bState === 'approved';
  const fallbackLabel = isApproved 
    ? 'Estimated Total' 
    : (window.WhizShop?.pricesShownOnceApprovedLabel || 'Prices shown once account is approved');

  const labelEls = document.querySelectorAll('#whiz-b2b-cart-total-label, .whiz-b2b-cart-total-label');
  const valueEls = document.querySelectorAll('#whiz-b2b-cart-total-value, .whiz-b2b-cart-total-value');

  if (!cart || !cart.items || cart.items.length === 0) {
    console.log('[Delify] Resetting B2B cart pricing total display.');
    labelEls.forEach(el => { el.innerHTML = fallbackLabel; });
    valueEls.forEach(el => {
      el.innerHTML = '';
      el.style.setProperty('display', 'none', 'important');
    });
    document.querySelectorAll('.whiz-cart-discount-notice').forEach(el => {
      el.style.display = 'none';
    });
    document.querySelectorAll('#checkout, button[name="checkout"], .b2b-btn-approved').forEach(el => {
      el.style.opacity = '1';
      el.style.pointerEvents = 'auto';
    });
    return;
  }

  // Create mapping of SKU -> pricing & detect company discounts
  let hasCompanyDiscounts = false;
  const pricingMap = {};
  if (items && items.length > 0) {
    items.forEach(item => {
      if (item.sku) {
        pricingMap[item.sku] = item;
      }
      if (item && item.unit_price !== null && item.unit_price > 0) {
        const netUnitPrice = item.unit_price;
        const origUnitPrice = (item.original_unit_price != null && item.original_unit_price > 0)
          ? item.original_unit_price
          : null;
        const unitDiff = origUnitPrice ? origUnitPrice - netUnitPrice : 0;
        const discountPercent = item.discount_percent != null
          ? item.discount_percent
          : (origUnitPrice && origUnitPrice > 0 ? (unitDiff / origUnitPrice) * 100 : 0);

        if (origUnitPrice && origUnitPrice > netUnitPrice && isSubstantialDiscount(unitDiff, discountPercent)) {
          hasCompanyDiscounts = true;
        }
      }
    });
  }

  console.log('[Delify] Pricing map constructed:', JSON.stringify(pricingMap), 'hasCompanyDiscounts:', hasCompanyDiscounts);

  // Update DOM rows using data-key across all matching tables (drawer & cart page)
  cart.items.forEach(cartItem => {
    const rows = document.querySelectorAll(`[data-key="${cartItem.key}"]`);
    console.log(`[Delify] Looking for row key ${cartItem.key}: found ${rows.length} rows`);
    if (rows.length === 0) return;

    const pricing = pricingMap[cartItem.sku];

    rows.forEach(row => {
      const unitPriceEl = row.querySelector('.whiz-b2b-unit-price');
      const linePriceEl = row.querySelector('.whiz-b2b-line-price');

      if (unitPriceEl) unitPriceEl.style.opacity = '1';
      if (linePriceEl) linePriceEl.style.opacity = '1';

      if (pricing && pricing.unit_price !== null && pricing.unit_price > 0) {
        const netUnitPrice = pricing.unit_price;
        const origUnitPrice = (pricing.original_unit_price != null && pricing.original_unit_price > 0)
          ? pricing.original_unit_price
          : null;
        const unitDiff = origUnitPrice ? origUnitPrice - netUnitPrice : 0;
        const discountPercent = pricing.discount_percent != null
          ? pricing.discount_percent
          : (origUnitPrice && origUnitPrice > 0 ? (unitDiff / origUnitPrice) * 100 : 0);

        const hasSubstantialDiscount = Boolean(
          origUnitPrice &&
          origUnitPrice > netUnitPrice &&
          isSubstantialDiscount(unitDiff, discountPercent)
        );

        if (hasSubstantialDiscount) {
          hasCompanyDiscounts = true;
        }

        // 1. Line-Item Unit Price Display
        if (unitPriceEl) {
          if (hasSubstantialDiscount) {
            unitPriceEl.innerHTML = `
              <span class="whiz-b2b-unit-price-wrap">
                <s class="whiz-b2b-compare-price">${formatPrice(origUnitPrice, currency)}</s>
                <span class="whiz-b2b-net-price">${formatPrice(netUnitPrice, currency)} / pc</span>
                <span class="whiz-b2b-discount-badge">-${formatDiscountPercent(discountPercent)}%</span>
              </span>
            `;
          } else {
            unitPriceEl.innerHTML = `<span>${formatPrice(netUnitPrice, currency)} / pc</span>`;
          }
        }

        // 2. Line Total Display
        if (linePriceEl) {
          const netLineTotal = (pricing.line_price != null && pricing.line_price > 0)
            ? pricing.line_price
            : netUnitPrice * cartItem.quantity;
          const origLineTotal = (pricing.original_line_price != null && pricing.original_line_price > 0)
            ? pricing.original_line_price
            : (origUnitPrice ? origUnitPrice * cartItem.quantity : null);

          if (hasSubstantialDiscount && origLineTotal && origLineTotal > netLineTotal) {
            linePriceEl.innerHTML = `
              <div class="whiz-b2b-line-price-wrap">
                <s class="whiz-b2b-compare-price">${formatPrice(origLineTotal, currency)}</s>
                <span class="whiz-b2b-line-net-price">${formatPrice(netLineTotal, currency)}</span>
              </div>
            `;
          } else {
            linePriceEl.innerHTML = `<span class="whiz-b2b-line-net-price">${formatPrice(netLineTotal, currency)}</span>`;
          }
        }
      } else {
        // Fallback for unmapped items
        if (unitPriceEl) {
          unitPriceEl.innerHTML = isApproved ? 'Contact Sales' : fallbackLabel;
        }
        if (linePriceEl) {
          linePriceEl.innerHTML = '';
        }
      }
    });
  });

  // Update Cart Subtotal (strikethrough original subtotal + actual net subtotal)
  if (subtotal !== null && subtotal > 0) {
    labelEls.forEach(el => { el.innerHTML = 'Estimated Total'; });

    // Calculate cart discount metrics
    const resolvedDiscount = discountTotal != null
      ? discountTotal
      : (originalSubtotal && originalSubtotal > subtotal ? originalSubtotal - subtotal : 0);
    const resolvedOriginalSubtotal = originalSubtotal || (resolvedDiscount > 0 ? subtotal + resolvedDiscount : null);
    const cartDiscountPercent = (resolvedOriginalSubtotal && resolvedOriginalSubtotal > 0)
      ? (resolvedDiscount / resolvedOriginalSubtotal) * 100
      : 0;

    const hasSubstantialCartDiscount = Boolean(
      resolvedDiscount > 0 &&
      resolvedOriginalSubtotal &&
      resolvedOriginalSubtotal > subtotal &&
      isSubstantialDiscount(resolvedDiscount, cartDiscountPercent)
    );

    if (hasSubstantialCartDiscount) {
      hasCompanyDiscounts = true;
    }

    if (hasSubstantialCartDiscount) {
      valueEls.forEach(el => {
        el.style.opacity = '1';
        el.innerHTML = `
          <span class="whiz-b2b-total-value-wrap">
            <s class="whiz-b2b-compare-price">${formatPrice(resolvedOriginalSubtotal, currency)}</s>
            <span class="whiz-b2b-total-net-price">${formatPrice(subtotal, currency)}</span>
          </span>
        `;
        el.style.setProperty('display', 'inline-block', 'important');
      });
    } else {
      valueEls.forEach(el => {
        el.style.opacity = '1';
        el.innerHTML = `<span class="whiz-b2b-total-net-price">${formatPrice(subtotal, currency)}</span>`;
        el.style.setProperty('display', 'inline-block', 'important');
      });
    }
  } else {
    labelEls.forEach(el => { el.innerHTML = fallbackLabel; });
    valueEls.forEach(el => {
      el.innerHTML = '';
      el.style.setProperty('display', 'none', 'important');
    });
  }

  // Option B: Toggle company discount notice display
  const noticeEls = document.querySelectorAll('.whiz-cart-discount-notice');
  noticeEls.forEach(el => {
    if (isApproved && hasCompanyDiscounts) {
      el.style.display = 'block';
    } else {
      el.style.display = 'none';
    }
  });

  // Restore Checkout Button
  const checkoutBtns = document.querySelectorAll('#checkout, button[name="checkout"], .b2b-btn-approved');
  checkoutBtns.forEach(el => {
    el.style.opacity = '1';
    el.style.pointerEvents = 'auto';
  });
}

// Currency formatter helper
function formatPrice(value, currencyCode) {
  const currency = currencyCode || 'SEK';
  try {
    const locale = window.Shopify?.locale || document.documentElement.lang || 'sv-SE';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(value);
  } catch (e) {
    console.error('[Delify] Error formatting currency:', e);
    return `${value} ${currency}`;
  }
}

